import {
  AutomationVariableDataType,
  GenericOption,
} from 'pages/AutomationEngine/types';
import { TFunction } from 'react-i18next';

type VariableDataTypeInUse = Extract<
  AutomationVariableDataType,
  'boolean' | 'date' | 'datetime' | 'number'
>;

type FormatMetaData = GenericOption<AutomationVariableDataType> & {
  meta: {
    output_format: (GenericOption & { format?: string })[];
    default_output_format: string;
  };
};

export type VariableNumberFormatTypes = 'include_comma' | 'no_comma';

type VariableFormatMetaData = Record<VariableDataTypeInUse, FormatMetaData>;

const isInUse = (
  type: AutomationVariableDataType
): type is VariableDataTypeInUse => Object.hasOwn(variableFormatMetaData, type);

export const getVariableFormatMetaData = (type: AutomationVariableDataType) =>
  isInUse(type) ? variableFormatMetaData[type] : undefined;

export const variableFormatMetaData: VariableFormatMetaData = {
  ['boolean']: {
    value: 'boolean',
    label: 'Boolean',
    meta: {
      output_format: [
        {
          value: 'true_false',
          label: 'True/False',
        },
        {
          value: 'yes_no',
          label: 'Yes/No',
        },
        {
          value: 'on_off',
          label: 'On/Off',
        },
      ],
      default_output_format: 'yes_no',
    },
  },
  ['date']: {
    value: 'date',
    label: 'Date',
    meta: {
      output_format: [
        {
          value: '%m/%d/%Y',
          label: 'MM/DD/YYYY',
          format: 'MM/dd/yyyy',
        },
        {
          value: '%m-%d-%Y',
          label: 'MM-DD-YYYY',
          format: 'MM-dd-yyyy',
        },
        {
          value: '%m.%d.%Y',
          label: 'MM.DD.YYYY',
          format: 'MM.dd.yyyy',
        },
        {
          value: '%d %b %Y',
          label: 'd MMM YYYY',
          format: 'd MMM yyyy',
        },
        {
          value: '%d/%m/%Y',
          label: 'DD/MM/YYYY',
          format: 'dd/MM/yyyy',
        },
        {
          value: '%d-%m-%Y',
          label: 'DD-MM-YYYY',
          format: 'dd-MM-yyyy',
        },
        {
          value: '%d.%m.%Y',
          label: 'DD.MM.YYYY',
          format: 'dd.MM.yyyy',
        },
        {
          value: '%Y/%m/%d',
          label: 'YYYY/MM/DD',
          format: 'yyyy/MM/dd',
        },
        {
          value: '%Y-%m-%d',
          label: 'YYYY-MM-DD',
          format: 'yyyy-MM-dd',
        },
        {
          value: '%Y.%m.%d',
          label: 'YYYY.MM.DD',
          format: 'yyyy.MM.dd',
        },
      ],
      default_output_format: '%m/%d/%Y',
    },
  },
  ['datetime']: {
    value: 'datetime',
    label: 'DateTime',
    meta: {
      output_format: [
        {
          value: '%m-%d-%Y %I:%M %p',
          label: 'MM-dd-yyyy hh:mm a z',
          format: 'MM-dd-yyyy hh:mm a xx',
        },
        {
          value: '%Y-%m-%d %H:%M',
          label: 'yyyy-MM-dd HH:mmz',
          format: 'yyyy-MM-dd HH:mmxx',
        },
        {
          value: '%m/%d/%Y %H:%M',
          label: 'MM/dd/yyyy HH:mmz',
          format: 'MM/dd/yyyy HH:mmxx',
        },
        {
          value: '%m/%d/%Y %I:%M %p',
          label: 'MM/dd/yyyy hh:mm a z',
          format: 'MM/dd/yyyy hh:mm a xx',
        },
        {
          value: '%Y-%m-%dT%H:%M',
          label: "yyyy-MM-dd'T'HH:mmz",
          format: "yyyy-MM-dd'T'HH:mmxx",
        },
        {
          value: '%d %b %Y %I:%M %p',
          label: 'dd MMM yyyy hh:mm a z',
          format: 'dd MMM yyyy hh:mm a xx',
        },
        {
          value: '%m.%d.%Y %I:%M %p',
          label: 'MM.dd.yyyy hh:mm a z',
          format: 'MM.dd.yyyy hh:mm a xx',
        },
      ],
      default_output_format: '%m/%d/%Y %I:%M %p',
    },
  },
  ['number']: {
    value: 'number',
    label: 'Number',
    meta: {
      output_format: [
        {
          value: 'include_comma',
          label: 'With commas',
        },
        {
          value: 'no_comma',
          label: 'Without commas',
        },
      ],
      default_output_format: 'no_comma',
    },
  },
};

export const getFormatString = (
  formatMetaData: FormatMetaData,
  format: string
) => {
  let formatString = formatMetaData.meta.output_format.find(
    ({ value }) => value === format
  );
  if (!formatString) {
    formatString = formatMetaData.meta.output_format.find(
      ({ value }) => value === formatMetaData.meta.default_output_format
    );
  }
  return formatString?.format || formatString?.label;
};

export const getBooleanString = (
  formatMetaData: FormatMetaData,
  format: string,
  value: boolean
) => {
  return getFormatString(formatMetaData, format)?.split('/')[value ? 0 : 1];
};

export const getNumberString = (
  num: string,
  formatString: VariableNumberFormatTypes = 'include_comma'
) => {
  const formated = Number(num.replaceAll(',', ''));

  return isNaN(formated)
    ? num
    : formatString === 'include_comma'
      ? formated.toLocaleString()
      : formated.toString();
};

export const getWebhookValueLabel = (value: string, t: TFunction) => {
  switch (value) {
    case 'meta_query_string':
      return t('URL Query String');
    case 'meta_body_content':
      return t('POST Body');
    default:
      return value;
  }
};
